import { useId, useRef } from 'react';

import { ChevronDownIcon, ChevronUpIcon, PlusIcon, Trash2Icon } from 'lucide-react';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { newQuestionOption } from '@/redux/dialogs/helper';
import { QuestionType } from '@/shared/types/action';
import { QUESTION_SET_LIMITS } from '@/util/validation/constants';
import {
  QUESTION_TYPE_LABEL_KEYS,
  QuestionSetForm,
} from '@/util/validation/schemas/question-set-schemas';

import { OptionFields } from './OptionFields';
import { ShowIfFields } from './ShowIfFields';

type Props = {
  index: number;
  count: number;
  onCommit: () => void;
  onRemove: (index: number) => void;
  onRemoveOption: (questionIndex: number, optionIndex: number) => void;
  onRenameOptionValue: (questionIndex: number, from: string, to: string) => void;
  onRenameKey: (from: string, to: string) => void;
  onMove: (index: number, to: number) => void;
  onTypeChange: (index: number, type: QuestionType) => void;
};

export const QuestionFields = ({
  index,
  count,
  onCommit,
  onRemove,
  onRemoveOption,
  onRenameOptionValue,
  onRenameKey,
  onMove,
  onTypeChange,
}: Props) => {
  const { t } = useTranslation();
  const { control, getValues } = useFormContext<QuestionSetForm>();
  const path = `questions.${index}` as const;
  // Once per edit: a key passed while typing may belong to another question.
  const keyAtFocus = useRef('');
  const optionsLabelId = useId();
  const type = useWatch({ control, name: `${path}.type` });
  const { fields: options, append } = useFieldArray({ control, name: `${path}.options` });

  const handleAddOption = () => {
    append(newQuestionOption(getValues(`${path}.options`) ?? []));
    onCommit();
  };

  return (
    <div className="flex flex-col gap-3 rounded-lg border border-border p-3">
      <div className="flex items-center justify-between">
        <span className="text-sm leading-5 font-semibold">
          {t('dialog.question_set.question_n', { 0: index + 1 })}
        </span>
        <div className="flex items-center">
          <Button
            type="button"
            variant="ghost-neutral"
            size="icon-sm"
            aria-label={t('dialog.question_set.move_up')}
            disabled={index === 0}
            onClick={() => onMove(index, index - 1)}
          >
            <ChevronUpIcon size={14} />
          </Button>
          <Button
            type="button"
            variant="ghost-neutral"
            size="icon-sm"
            aria-label={t('dialog.question_set.move_down')}
            disabled={index === count - 1}
            onClick={() => onMove(index, index + 1)}
          >
            <ChevronDownIcon size={14} />
          </Button>
          <Button
            className="hover:text-destructive hover:[&_svg]:text-destructive"
            type="button"
            variant="ghost-neutral"
            size="icon-sm"
            aria-label={t('dialog.question_set.remove_question')}
            disabled={count === 1}
            onClick={() => onRemove(index)}
          >
            <Trash2Icon size={14} />
          </Button>
        </div>
      </div>

      <FormField
        control={control}
        name={`${path}.question`}
        render={({ field }) => (
          <FormItem>
            <FormLabel>{t('dialog.question_set.question')}</FormLabel>
            <FormControl>
              <Input {...field} maxLength={QUESTION_SET_LIMITS.QUESTION} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={control}
        name={`${path}.header`}
        render={({ field }) => (
          <FormItem>
            <FormLabel>{t('dialog.question_set.header')}</FormLabel>
            <FormControl>
              <Input {...field} maxLength={QUESTION_SET_LIMITS.HEADER} />
            </FormControl>
            <FormMessage />
            <FormDescription>{t('dialog.question_set.header_description')}</FormDescription>
          </FormItem>
        )}
      />

      <FormField
        control={control}
        name={`${path}.key`}
        render={({ field }) => (
          <FormItem>
            <FormLabel>{t('dialog.question_set.key')}</FormLabel>
            <FormControl>
              <Input
                {...field}
                maxLength={QUESTION_SET_LIMITS.KEY}
                onFocus={() => {
                  keyAtFocus.current = field.value;
                }}
                onBlur={() => {
                  onRenameKey(keyAtFocus.current, field.value);
                  field.onBlur();
                }}
              />
            </FormControl>
            <FormMessage />
            <FormDescription>
              {t('dialog.question_set.key_description', { 0: field.value })}
            </FormDescription>
          </FormItem>
        )}
      />

      <FormField
        control={control}
        name={`${path}.type`}
        render={({ field }) => (
          <FormItem>
            <FormLabel>{t('dialog.question_set.type')}</FormLabel>
            <Select
              value={field.value}
              onValueChange={(value) => onTypeChange(index, value as QuestionType)}
            >
              <FormControl>
                <SelectTrigger aria-label={t('dialog.question_set.type')}>
                  <SelectValue />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                {Object.entries(QUESTION_TYPE_LABEL_KEYS).map(([questionType, labelKey]) => (
                  <SelectItem key={questionType} value={questionType}>
                    {t(labelKey)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>
        )}
      />

      {type !== 'text' && (
        <>
          <FormField
            control={control}
            name={`${path}.allow_other`}
            render={({ field }) => (
              <FormItem className="flex flex-row items-center justify-between gap-4">
                <div className="space-y-0.5">
                  <FormLabel>{t('dialog.question_set.allow_other')}</FormLabel>
                  <FormDescription>
                    {t('dialog.question_set.allow_other_description')}
                  </FormDescription>
                </div>
                <FormControl>
                  <Switch
                    checked={Boolean(field.value)}
                    onCheckedChange={(checked) => {
                      field.onChange(checked);
                      onCommit();
                    }}
                  />
                </FormControl>
              </FormItem>
            )}
          />

          <FormField
            control={control}
            name={`${path}.options`}
            render={() => (
              <FormItem>
                <FormLabel id={optionsLabelId}>{t('dialog.question_set.options')}</FormLabel>
                <FormControl>
                  <div
                    role="group"
                    aria-labelledby={optionsLabelId}
                    className="flex flex-col gap-2"
                  >
                    {options.map((option, optionIndex) => (
                      <OptionFields
                        key={option.id}
                        questionIndex={index}
                        optionIndex={optionIndex}
                        canRemove={options.length > QUESTION_SET_LIMITS.OPTIONS_MIN}
                        onCommit={onCommit}
                        onRemove={(optionIndex) => onRemoveOption(index, optionIndex)}
                        onRenameValue={(from, to) => onRenameOptionValue(index, from, to)}
                      />
                    ))}
                  </div>
                </FormControl>
                <FormMessage />
                <Button
                  type="button"
                  variant="outline-neutral"
                  size="sm"
                  disabled={options.length >= QUESTION_SET_LIMITS.OPTIONS_MAX}
                  onClick={handleAddOption}
                >
                  <PlusIcon size={14} />
                  {t('dialog.question_set.add_option')}
                </Button>
              </FormItem>
            )}
          />
        </>
      )}

      <ShowIfFields name={`${path}.show_if`} questionIndex={index} onCommit={onCommit} />
    </div>
  );
};
